import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import LoadingSpinner from '../components/LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const SamlCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setTokens, setUser } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;

    const processCallback = async () => {
      const accessToken =
        searchParams.get('access_token') || searchParams.get('token');
      const needsOnboarding = searchParams.get('needs_onboarding') === 'true';
      const redirect = searchParams.get('redirect') || '/app/dashboard';
      const errorParam = searchParams.get('error');

      if (errorParam) {
        const messages: Record<string, string> = {
          invalid_response:
            'SAML response could not be validated (signature or format). If ACS URL and Entity ID match, check backend logs; for samltest, map attributes: email, firstName, lastName.',
          no_email:
            'Identity provider did not send an email. Check your attribute mapping.',
          missing_response: 'No SAML response received from the identity provider.',
          org_not_found: 'Organization not found or SSO is disabled.',
          plan_required: 'SSO is only available on the Business plan.',
        };
        setError(messages[errorParam] || 'SSO sign-in failed. Contact your administrator.');
        setTimeout(() => navigate('/login'), 5000);
        return;
      }

      if (!accessToken) {
        setError('Could not retrieve session information from the identity provider.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      processedRef.current = true;

      try {
        const profileResponse = await fetch(`${API_BASE_URL}/auth/profile/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (!profileResponse.ok) {
          throw new Error('Failed to fetch user profile');
        }

        const user = await profileResponse.json();
        setTokens({ access: accessToken, refresh: '' });
        setUser(user);

        const targetPath = needsOnboarding ? '/app/subscription' : redirect;
        navigate(targetPath, { replace: true });
      } catch {
        setError('SSO sign-in could not be completed. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processCallback();
  }, [searchParams, setTokens, setUser, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">SSO Sign-In Failed</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <p className="text-sm text-gray-500 dark:text-gray-500">Redirecting you to the sign-in page…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-600 dark:text-gray-400">Completing SSO sign-in…</p>
      </div>
    </div>
  );
};

export default SamlCallbackPage;
